<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { formatHashrate, formatRelativeDate, getBreakpoint } from "@/utils";
import ContractNavigationWrapper from "@/components/ContractNavigationWrapper";

export default {
  // Included components
  components: {
    FontAwesomeIcon,
    ContractNavigationWrapper,
  },

  data() {
    return {
      // Number of contracts to show on each page / current request limit
      PAGE_SIZE: 20,

      // Current request limit
      limit: 20,

      // current request offset
      offset: 0,

      // The current page number (1 indexed)
      currentPage: null,

      // current contracts accessible on the page (currently or through pagination)
      currentContractFacilities: [],

      // Whether there is another page of data
      hasNextPage: false,

      // total number of pages
      totalPages: 0,

      // Whether list view or card view is active
      listView: true,

      // search input
      searchQuery: "",

      // whether data is currently being fetched
      loadingState: true,
    };
  },

  computed: {
    // Mapped vuex state
    ...mapGetters(["can"]),
    ...mapState(["user"]),
  },

  async created() {
    if (!this.user.isStaffAdmin) {
      await this.$router.push("/");
    }

    this.loadingState = true;
    var result = await this.getContractFacilities();
    this.currentContractFacilities = result;
    this.loadingState = false;
  },

  methods: {
    // Helper methods and vuex actions
    formatRelativeDate,
    formatHashrate,
    getBreakpoint,
    ...mapActions(["getContractFacilities"]),
  },
};
</script>
<template>
  <ContractNavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/contract-facilities" active>
        Contract Facilities
      </b-breadcrumb-item>
    </template>
    <b-row class="Toolbar mb-3">
      <b-col>
        <h1>Contract Facilities</h1>
      </b-col>

      <b-col
        class="text-right"
        align-self="center"
        v-if="
          can.workers.edit &&
          !this.loadingState &&
          this.currentContractFacilities !== 0
        "
      >
        <span
          id="disabled-wrapper"
          class="d-inline-block float-right"
          tabindex="0"
        >
          <b-button
            v-if="can.groups.edit"
            :class="getBreakpoint().xs ? 'mt-2 ml-1' : 'ml-2'"
            variant="primary"
            to="/contract-facilities/create"
            :size="getBreakpoint().xs ? 'sm' : 'md'"
          >
            {{ getBreakpoint().xs ? "New" : "New Contract Facility" }}
          </b-button>
        </span>
      </b-col>
    </b-row>

    <b-row align-v="center">
      <b-col
        cols="3"
        align-self="center"
        class="mb-3"
        v-if="getBreakpoint().xs"
      >
        <b-button
          variant="light"
          :pressed.sync="listView"
          v-if="getBreakpoint().xs"
        >
          <FontAwesomeIcon icon="list" size="lg" />
        </b-button>
      </b-col>
    </b-row>

    <b-row v-if="loadingState">
      <b-col class="text-center mt-5">
        <div class="mb-4">
          <b-spinner
            style="width: 4rem; height: 4rem"
            variant="primary"
            label="Loading Contract Facilities..."
          ></b-spinner>
        </div>
        <b>Loading Contract Facilities...</b>
      </b-col>
    </b-row>

    <b-row v-else-if="this.currentContractFacilities.length === 0">
      <b-col>
        <b-card class="text-center py-5">
          <b-card-title>No Contract Facilities</b-card-title>
          <b-card-text>Create new contract facility</b-card-text>
          <b-button
            v-if="can.groups.edit"
            :class="getBreakpoint().xs ? 'mt-2 ml-1' : 'ml-2'"
            variant="primary"
            to="/contract-facilities/create"
            :size="getBreakpoint().xs ? 'sm' : 'md'"
          >
            {{ getBreakpoint().xs ? "New" : "New Contract Facility" }}
          </b-button>
        </b-card>
      </b-col>
    </b-row>

    <b-row v-else-if="listView && !loadingState">
      <b-col>
        <b-table-simple responsive striped hover>
          <b-thead>
            <b-tr>
              <b-th>Facility Name</b-th>
              <b-th>Facility Handle</b-th>
              <b-th>Contact Email</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="(contractFacility, index) in currentContractFacilities"
              :key="index"
            >
              <b-td>
                <b-link :to="`/contract-facilities/${contractFacility.id}`">
                  {{ contractFacility.name }}
                </b-link>
              </b-td>
              <b-td>{{ contractFacility.handle }}</b-td>
              <b-td>{{ contractFacility.contact_email }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-col>
    </b-row>

    <b-pagination-nav
      v-if="this.totalPages > 1 && !loadingState"
      :number-of-pages="this.totalPages"
      base-url="?page="
      align="center"
      limit="7"
      size="sm"
      v-model="currentPage"
      :use-router="true"
      :hide-goto-end-buttons="this.totalPages < 3"
    ></b-pagination-nav>
  </ContractNavigationWrapper>
</template>

<style scoped>
.Worker-card {
  margin-bottom: 2rem;
}

.Worker-card-title {
  display: flex;
  align-items: center;
}

.Worker-card .Worker-card-link-indicator {
  display: inline-flex;
  align-items: center;

  opacity: 0;

  transition: all 200ms;
}

.Worker-card:hover .Worker-card-link-indicator {
  opacity: 1;
  margin-left: 0.35rem;
}

.Worker-card a {
  color: unset;
  font-weight: unset;
  text-decoration: unset;
}

.Worker-card:hover .Worker-card-title {
  color: #007bff;
}

#listview {
  display: inline-block;
  margin-top: 7px;
}

#search-button {
  border: 1px solid transparent;
  box-shadow: 0 0 2px 0 rgba(0, 20, 40, 0.4);
}

#list-more:hover {
  cursor: pointer;
}
</style>
